// Decoders for the WPM operating-status register (Modbus reg 2501).
//
// The firmware packs that bit-coded register into the HIGH 16 bits of the
// heat_pump `fault_bitmap` (see firmware wpm_client.c:
//   fault_bitmap = (op_status << 16) | <low-byte fault flags>).
// So the live status bits are `fault_bitmap >>> 16`. Bit positions per
// contracts/modbus-wpm.md reg 2501.

// Bit B0 = HC1 heating-circuit pump running — the slab circulation pump that
// pushes water through the floor loops (the WPM unit's "heating" symbol).
export const WPM_STATUS_HC1_PUMP = 1 << 0;
// Bit B6 = compressor running — the heat pump is actively making heat.
export const WPM_STATUS_COMPRESSOR = 1 << 6;

/** The raw WPM operating-status register (reg 2501) carried in fault_bitmap. */
export function wpmOperatingStatus(faultBitmap: number): number {
  // >>> keeps it an unsigned 32-bit shift; mask back to the 16-bit register.
  return (faultBitmap >>> 16) & 0xffff;
}

/** True when the slab (HC1) circulation pump is running. */
export function isSlabPumping(faultBitmap: number): boolean {
  return (wpmOperatingStatus(faultBitmap) & WPM_STATUS_HC1_PUMP) !== 0;
}

/** True when the heat-pump compressor is running (actively making heat). */
export function isCompressorRunning(faultBitmap: number): boolean {
  return (wpmOperatingStatus(faultBitmap) & WPM_STATUS_COMPRESSOR) !== 0;
}

export interface WpmActivity {
  label: string;
  tone: 'heat' | 'cool' | 'none'; // maps to .tag.heat / .tag.cool / .tag.none
  pumping: boolean; // slab (HC1) pump circulating water to the floors
}

// What the heat pump is doing for the house: a friendly label for its
// compressor mode (a plain "off" becomes "idle"), plus whether the slab pump
// is actively circulating — shown as a separate highlight so you can see both
// "hot water" AND "pumping" at the same time.
export function deriveActivity(hp: { current_mode: string; fault_bitmap: number }): WpmActivity {
  const pumping = isSlabPumping(hp.fault_bitmap);
  switch (hp.current_mode) {
    case 'heating': return { label: 'heating', tone: 'heat', pumping };
    case 'cooling': return { label: 'cooling', tone: 'cool', pumping };
    case 'defrost': return { label: 'defrosting', tone: 'cool', pumping };
    case 'dhw':     return { label: 'hot water', tone: 'heat', pumping };
    default:        return { label: 'idle', tone: 'none', pumping }; // 'off'
  }
}
